"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Store, ShoppingBag, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PALETTE } from "../lib/colorPallete";

const STORAGE_KEY = "fortune_wholesale_popup_dismissed";

interface WholesalePopupProps {
  onSelectWholesale: () => void;
}

const WholesalePopup: React.FC<WholesalePopupProps> = ({
  onSelectWholesale,
}) => {
  const { user, isLoading } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!hasMounted) return;
    if (isLoading) return;
    if (user) {
      setIsOpen(false);
      return;
    }
    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    if (dismissed) {
      return;
    }
    const timer = setTimeout(() => setIsOpen(true), 300);
    return () => clearTimeout(timer);
  }, [user, isLoading, hasMounted]);

  const handleRetailContinue = () => {
    sessionStorage.setItem(STORAGE_KEY, "true");
    setIsOpen(false);
  };

  const handleWholesaleSelect = () => {
    setIsOpen(false);
    onSelectWholesale();
  };

  const handleClose = () => {
    sessionStorage.setItem(STORAGE_KEY, "true");
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 z-100 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            {}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full text-stone-400 hover:bg-stone-100 transition-colors z-10"
            >
              <X size={20} />
            </button>

            {}
            <div
              className="p-6 pb-4 text-center"
              style={{ backgroundColor: `${PALETTE.darkGreen}10` }}
            >
              <div
                className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: PALETTE.gold }}
              >
                <Store size={32} style={{ color: PALETTE.darkerGreen }} />
              </div>
              <h2
                className="text-2xl font-bold font-serif"
                style={{ color: PALETTE.darkGreen }}
              >
                Welcome to Fortune Agro!
              </h2>
              <p className="text-stone-600 mt-2">Are you a wholesale buyer?</p>
            </div>

            {}
            <div className="p-6 space-y-3">
              <button
                onClick={handleWholesaleSelect}
                className="w-full p-4 rounded-xl border-2 text-left transition-all hover:shadow-md group"
                style={{
                  borderColor: PALETTE.darkGreen,
                  backgroundColor: "white",
                }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${PALETTE.darkGreen}15` }}
                  >
                    <Store size={24} style={{ color: PALETTE.darkGreen }} />
                  </div>
                  <div>
                    <p
                      className="font-semibold text-lg"
                      style={{ color: PALETTE.darkGreen }}
                    >
                      Yes, I&apos;m a Wholesaler
                    </p>
                    <p className="text-sm text-stone-500">
                      Register for exclusive wholesale pricing
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={handleRetailContinue}
                className="w-full p-4 rounded-xl border-2 border-stone-200 text-left transition-all hover:border-stone-300 hover:shadow-md group bg-white"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-stone-100 shrink-0">
                    <ShoppingBag size={24} className="text-stone-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg text-stone-700">
                      No, Continue as Retail
                    </p>
                    <p className="text-sm text-stone-500">
                      Browse our products at retail prices
                    </p>
                  </div>
                </div>
              </button>
            </div>

            {}
            <div className="px-6 pb-6">
              <p className="text-xs text-center text-stone-400">
                You can always change this later in your account settings
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WholesalePopup;
